import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useSaveTag, type Tag } from '@/hooks/useTags'
import { useT } from '@/i18n/I18nProvider'

export function TagFormDialog({
  open,
  onOpenChange,
  tag,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  tag?: Tag | null
}) {
  const { t } = useT()
  const save = useSaveTag()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  useEffect(() => {
    if (!open) return
    setName(tag?.name ?? '')
    setDescription(tag?.description ?? '')
  }, [open, tag])

  async function submit() {
    if (!name.trim()) return toast.error(t('tagf.errName'))
    try {
      await save.mutateAsync({
        id: tag?.id,
        oldName: tag?.name,
        values: { name: name.trim(), description: description.trim() || null },
      })
      toast.success(tag ? t('tagf.updated') : t('tagf.created'))
      onOpenChange(false)
    } catch (e) {
      toast.error(t('tagf.errSave'), { description: (e as Error).message })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{tag ? t('tagf.editTitle') : t('tagf.newTitle')}</DialogTitle>
          <DialogDescription>{t('tagf.desc')}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label htmlFor="tag-name">{t('tagf.name')}</Label>
            <Input id="tag-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: DOCAVISA" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="tag-desc">{t('tagf.descLabel')}</Label>
            <Input
              id="tag-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('tagf.descPlaceholder')}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={save.isPending}>
            {save.isPending ? t('common.saving') : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
